'use client';

import { useMemo, useState } from 'react';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useAccount, useReadContracts } from 'wagmi';
import { formatEther } from 'viem';
import { abi as stickerChainAbi } from '@/app/slaps/StickerChainAbi';
import { abi as stickerDesignsAbi } from '@/app/stickers/StickerDesignsAbi';

const StickerChainContractAddress = process.env
  .NEXT_PUBLIC_STICKER_CHAIN_CONTRACT_ADDRESS as `0x${string}` | undefined;
const StickerDesignsContractAddress = process.env
  .NEXT_PUBLIC_STICKER_DESIGNS_CONTRACT_ADDRESS as `0x${string}` | undefined;
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as const;
const SLAP_SIZES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export default function FeesClient() {
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const [shouldLoadFees, setShouldLoadFees] = useState(false);
  const [slapSize, setSlapSize] = useState(1);

  const readsEnabled =
    shouldLoadFees &&
    isConnected &&
    Boolean(StickerChainContractAddress) &&
    Boolean(StickerDesignsContractAddress);

  const coreContracts = useMemo(
    () => [
      {
        abi: stickerChainAbi,
        address: StickerChainContractAddress || '0x0',
        functionName: 'playerReputationFee',
      },
      {
        abi: stickerChainAbi,
        address: StickerChainContractAddress || '0x0',
        functionName: 'initiatedPlayers',
        args: [address ?? ZERO_ADDRESS],
      },
      {
        abi: stickerDesignsAbi,
        address: StickerDesignsContractAddress || '0x0',
        functionName: 'publisherReputationFee',
      },
      {
        abi: stickerDesignsAbi,
        address: StickerDesignsContractAddress || '0x0',
        functionName: 'stickerRegistrationFee',
      },
      {
        abi: stickerDesignsAbi,
        address: StickerDesignsContractAddress || '0x0',
        functionName: 'isPublisherInGoodStanding',
        args: [address ?? ZERO_ADDRESS],
      },
    ] as const,
    [address],
  );

  const slapFeeContracts = useMemo(
    () =>
      SLAP_SIZES.map((size) => ({
        abi: stickerChainAbi,
        address: StickerChainContractAddress || '0x0',
        functionName: 'slapFeeForSize' as const,
        args: [BigInt(size)],
      })),
    [],
  );

  const {
    data: coreData,
    refetch: refetchCoreData,
  } = useReadContracts({
    contracts: coreContracts,
    allowFailure: false,
    query: {
      enabled: readsEnabled,
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  const {
    data: slapFeesData,
    refetch: refetchSlapFees,
  } = useReadContracts({
    contracts: slapFeeContracts,
    allowFailure: false,
    query: {
      enabled: readsEnabled,
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  const playerReputationFeeData = coreData?.[0];
  const playerInGoodStandingData = coreData?.[1];
  const publisherReputationFeeData = coreData?.[2];
  const stickerRegistrationFeeData = coreData?.[3];
  const publisherInGoodStandingData = coreData?.[4];

  const slapFeeBySize = useMemo(() => {
    const map: Record<number, bigint> = {};
    if (!slapFeesData) {
      return map;
    }
    SLAP_SIZES.forEach((size, index) => {
      const fee = slapFeesData[index];
      if (typeof fee === 'bigint') {
        map[size] = fee;
      }
    });
    return map;
  }, [slapFeesData]);

  const slapFeeForSizeData = slapFeeBySize[slapSize];

  const hasLoadedCoreFees =
    typeof slapFeeForSizeData === 'bigint' &&
    typeof playerReputationFeeData === 'bigint' &&
    typeof stickerRegistrationFeeData === 'bigint' &&
    typeof publisherReputationFeeData === 'bigint';

  const playerTotalLabel = useMemo(() => {
    if (!hasLoadedCoreFees) {
      return '--';
    }
    const slapFee = slapFeeForSizeData as bigint;
    const repFee = playerReputationFeeData as bigint;
    const hasPaidRep = Boolean(playerInGoodStandingData);
    const total = hasPaidRep ? slapFee : slapFee + repFee;
    return `${formatEther(total)} ETH`;
  }, [
    hasLoadedCoreFees,
    slapFeeForSizeData,
    playerReputationFeeData,
    playerInGoodStandingData,
  ]);

  const publisherTotalLabel = useMemo(() => {
    if (!hasLoadedCoreFees) {
      return '--';
    }
    const regFee = stickerRegistrationFeeData as bigint;
    const repFee = publisherReputationFeeData as bigint;
    const inGoodStanding = Boolean(publisherInGoodStandingData);
    const total = inGoodStanding ? regFee : regFee + repFee;
    return `${formatEther(total)} ETH`;
  }, [
    hasLoadedCoreFees,
    stickerRegistrationFeeData,
    publisherReputationFeeData,
    publisherInGoodStandingData,
  ]);

  const handleCheckFees = () => {
    if (!isConnected) {
      if (openConnectModal) {
        openConnectModal();
      }
      return;
    }

    if (shouldLoadFees) {
      void refetchCoreData();
      void refetchSlapFees();
      return;
    }

    setShouldLoadFees(true);
  };

  return (
    <div className="mt-8 rounded-3xl bg-white p-6 shadow-xl md:p-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
            StickerChain Fees
          </h1>
          <p className="mt-2 text-sm md:text-base text-slate-600">
            Each resource type has a one-time reputation fee and a per-unit creation fee.
          </p>
        </div>
        {!hasLoadedCoreFees && (
          <button
            type="button"
            onClick={handleCheckFees}
            className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition"
          >
            Check Current Fees
          </button>
        )}
      </div>

      {!isConnected && (
        <div className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          Connect your wallet to read current onchain fees.
        </div>
      )}

      {isConnected && !shouldLoadFees && (
        <div className="mt-4 text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded-xl px-3 py-2">
          Wallet connected. Click <span className="font-semibold">Check Current Fees</span> to load current fees from blockchain.
        </div>
      )}

      <div className="mt-8 grid gap-6">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <h2 className="text-lg font-semibold text-slate-900">For Players (Slaps)</h2>
          <div className="mt-3 text-sm">
            <div className="mt-4">
              First Time Player Fee:{' '}
              {typeof playerReputationFeeData === 'bigint'
                ? `${formatEther(playerReputationFeeData)} ETH`
                : '--'}
              <div className="italic mt-2">
                Each player pays this once to establish reputation.
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center gap-2">
                <span>Per-Slap Fee (size</span>
                <select
                  value={slapSize}
                  onChange={(e) => setSlapSize(Number(e.target.value))}
                  className="rounded border border-slate-300 bg-white px-2 py-1"
                >
                  {SLAP_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span>):</span>
                <span>
                  {typeof slapFeeForSizeData === 'bigint'
                    ? `${formatEther(slapFeeForSizeData)} ETH`
                    : '--'}
                </span>
              </div>
              <div className="italic mt-2">Paid on every slap creation.</div>
            </div>
            <div className="mt-4 font-semibold">Slap Cost: {playerTotalLabel}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <h2 className="text-lg font-semibold text-slate-900">For Artists (Sticker Publishing)</h2>
          <div className="text-sm">
            <div className="mt-4">
              First Time Publisher Fee:{' '}
              {typeof publisherReputationFeeData === 'bigint'
                ? `${formatEther(publisherReputationFeeData)} ETH`
                : '--'}
              <div className="italic mt-2">Each publisher pays this once to establish reputation.</div>
            </div>

            <div className="mt-4">
              Content Management Fee:{' '}
              {typeof stickerRegistrationFeeData === 'bigint'
                ? `${formatEther(stickerRegistrationFeeData)} ETH`
                : '--'}
              <div className="italic mt-2">Per sticker fee pays for file storage and content policy enforcement.</div>
            </div>

            <div className="mt-4 font-semibold">Your Publish Cost: {publisherTotalLabel}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
